import React, { type FC, useCallback, useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    MagnifyingGlassIcon, PlusIcon, TrashIcon, UserPlusIcon, CubeIcon, TruckIcon, ShoppingCartIcon, CakeIcon,
} from '@heroicons/react/24/outline';
import { EllipsisVerticalIcon, EyeIcon } from '@heroicons/react/24/solid';

import type { Customer, Address, OrderFormData, CustomerWithoutId, Order, DeliveryFormData } from '../types';
import { useOrders, useOrdersPageRealtimeRefresh } from '../hooks/useOrders';
import { useCustomers } from '../hooks/useCustomers';
import { useStores } from '../hooks/useStores';
import CreateOrderModal from '../components/CreateOrderModal';
import CreateDeliveryModal from '../components/CreateDeliveryModal';
import ConfirmModal from '../components/ConfirmModal';
import StatusBadge from '../components/StatusBadge';
import { fetchCakeOrderIdsByOrderIdsFromDb, fetchProductPriceRecordFromDb, insertOrderItemFromDb } from '../services/orderService';
import { geocodeAddress } from '../lib/geocode';
import OrderTypeBadge from '../components/OrderTypeBadge.tsx';
import TablePagination from '../components/TablePagination.tsx';
import { useToast } from '../hooks/useToast.ts';
import { NON_CANCELLABLE_STATUSES, STATUS_OPTIONS } from '../constants/orderStatuses';

interface OrdersProps { title?: string }

// Number of orders shown per page in the table.
const ITEMS_PER_PAGE = 25;

// Returns the list of quick-action buttons shown in each row's action menu,
// based on the order's current status. Each action deep-links to a specific
// tab on the order detail page or opens the assign-driver modal.
const getActionsByStatus = (status: string): Array<{
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    tab: 'overview' | 'details' | 'payments' | 'deliveries' | 'logs';
}> => {
    const s = status?.toUpperCase();
    const actions: Array<{ label: string; icon: React.ComponentType<{ className?: string }>; tab: 'overview' | 'details' | 'payments' | 'deliveries' | 'logs' }> = [];
    switch (s) {
        case 'AWAITING_PACKING':
            actions.push({ label: 'Assign Packer', icon: UserPlusIcon, tab: 'details' }); break;
        case 'PACKER_ASSIGNED':
            actions.push({ label: 'Complete Packing', icon: CubeIcon, tab: 'details' }); break;
        case 'READY_FOR_COLLECTION':
            actions.push({ label: 'View Collection', icon: CubeIcon, tab: 'overview' }); break;
        case 'PACKING_COMPLETE':
        case 'ASSIGN_DRIVER':
        case 'DELIVERY_FAILED':
            actions.push({ label: 'Assign Driver', icon: TruckIcon, tab: 'details' }); break;
        case 'DRIVER_ASSIGNED':
        case 'ON_ROUTE':
            actions.push({ label: 'Track Delivery', icon: TruckIcon, tab: 'deliveries' }); break;
    }
    return actions;
};

const Orders: FC<OrdersProps> = () => {

    // ── Hooks ──────────────────────────────────────────────────────────────────

    const {
        fetchOrdersPage,
        createOrder,
        updateOrderStatus,
        assignDriverToOrder,
        loading: orderLoading,
    } = useOrders();
    const { customers, fetchCustomers, createCustomer, createAddress, setCustomersPrimaryAddress, fetchAddressesForCustomer } = useCustomers();
    const [addresses, setAddresses] = useState<Address[]>([]);
    const { stores, fetchStores } = useStores();

    const navigate = useNavigate();
    const location = useLocation();
    const { error, success} = useToast();

    // ── Refs ───────────────────────────────────────────────────────────────────

    // Ref for the status filter dropdown container — used to detect clicks outside it.
    const statusDropdownRef = useRef<HTMLDivElement | null>(null);
    // Per-row refs for the ⋮ button and its portal menu, keyed by order ID.
    // Used to detect outside clicks and to reposition the menu on scroll/resize.
    const actionButtonRefs = useRef<Record<string | number, HTMLButtonElement | null>>({});
    const actionMenuRefs = useRef<Record<string | number, HTMLDivElement | null>>({});
    const [actionMenuPosition, setActionMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

    // ── State ──────────────────────────────────────────────────────────────────

    const [isModalOpen, setIsModalOpen] = useState<boolean>(() =>
        Boolean((location.state as { openCreate?: boolean } | null)?.openCreate)
    );
    // Guards to ensure customers and stores are only fetched once per session,
    // not on every re-open of the creation order modal.
    const [customersLoaded, setCustomersLoaded] = useState(false);
    const [storesLoaded, setStoresLoaded] = useState(false);
    // ID of the order whose ⋮ action menu is currently open, or null if none.
    const [dropdownOpenId, setDropdownOpenId] = useState<string | number | null>(null);
    const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
    const [loading, setLoading] = useState(false);

    // Cancel order confirmation modal state.
    const [orderToCancel, setOrderToCancel] = useState<Order | null>(null);
    const [cancelLoading, setCancelLoading] = useState(false);

    // Filter state — any change resets the page back to 1.
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilters, setStatusFilters] = useState<string[]>(() => {
        const incoming = (location.state as { statuses?: string[] } | null)?.statuses;
        return Array.isArray(incoming) && incoming.length > 0
            ? incoming.map(s => String(s).toUpperCase())
            : [];
    });
    const [typeFilter, setTypeFilter] = useState<string>('All');
    const [deliveryTypeFilter, setDeliveryTypeFilter] = useState<string>('All');
    const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);

    // 'open' shows active orders; 'delivered' shows archived/completed orders.
    const [activeTab, setActiveTab] = useState<'open' | 'delivered'>('open');

    // Pagination and table data.
    // `ordersPageRows` is null while loading (used to show the skeleton),
    // and an empty array when loaded, but no results match the current filters.
    const [currentPage, setCurrentPage] = useState(1);
    const [ordersPageRows, setOrdersPageRows] = useState<Order[] | null>(null);
    const [ordersTotalCount, setOrdersTotalCount] = useState(0);

    // Set of order IDs (as strings) that have an associated cake order.
    // Populated after each page loads via a secondary query.
    const [cakeOrderIds, setCakeOrderIds] = useState<Set<string>>(new Set());

    // Assign-driver modal state.
    const [isCreateDeliveryModalOpen, setIsCreateDeliveryModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const [newDelivery, setNewDelivery] = useState<DeliveryFormData>({ destination_id: '', driver_id: '' });

    // Create order modal form state.
    const [orderData, setOrderData] = useState<OrderFormData>({
        reference: '', parcel_count: 1, customer_id: '', delivery_address_id: '',
        type: '', notes: '', store_id: '', delivery_fee: 0, delivery_type: '', collection_point_id: '',
    });
    const [newCustomer, setNewCustomer] = useState<CustomerWithoutId>({
        first_name: '', last_name: '', cellphone: '', delivery_address: null,
        address: { address_1: '', address_2: '', suburb: '', city: '', postcode: '' },
    });

    // ── Helpers ────────────────────────────────────────────────────────────────

    // Calculates the pixel position for a portal-rendered action menu so it
    // appears directly below the ⋮ button without being clipped by the table.
    // Clamps the left edge to keep the menu within the viewport.
    const getMenuPosition = (button: HTMLButtonElement) => {
        const rect = button.getBoundingClientRect();
        const menuWidth = 192;
        return {
            top:  rect.bottom + 8,
            left: Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8)),
        };
    };

    // ── Data fetching ──────────────────────────────────────────────────────────

    // Fetches the current page of orders based on all active filters and tabs.
    // Sets ordersPageRows to null first so the skeleton table is shown during loads.
    // After loading, fires a secondary query to flag which orders have cake orders.
    const loadOrdersPage = useCallback(async () => {
        setOrdersPageRows(null);
        const result = await fetchOrdersPage({
            page: currentPage, pageSize: ITEMS_PER_PAGE, activeTab,
            statuses: statusFilters, type: typeFilter,
            deliveryType: deliveryTypeFilter, search: searchQuery,
        });
        const rows = result?.rows ?? [];
        setOrdersPageRows(rows);
        setOrdersTotalCount(result?.count ?? 0);

        // Fetch which of the loaded orders have a cake order attached,
        // so the cake badge can be shown inline without embedding it in the main query.
        if (rows.length > 0) {
            const orderIds = rows.map(o => String(o.id));
            setCakeOrderIds(await fetchCakeOrderIdsByOrderIdsFromDb(orderIds));
        } else {
            setCakeOrderIds(new Set());
        }
    }, [fetchOrdersPage, currentPage, activeTab, statusFilters, typeFilter, deliveryTypeFilter, searchQuery]);

    // Re-run loadOrdersPage whenever any filter, tab, or page dependency changes.
    useEffect(() => {
        void loadOrdersPage();
    }, [loadOrdersPage]);

    // Subscribe to realtime order changes so the table refreshes automatically
    // when an order status changes elsewhere (driver app, another admin tab).
    useOrdersPageRealtimeRefresh(loadOrdersPage);

    // Handle deep-link navigation state set by other pages:
    //   - `openCreate`: open the creation order modal immediately on arrival.
    //   - `statuses`: pre-populate the status filter (from the dashboard KPI cards).
    // The state is cleared from history after reading, so refreshing the page doesn't re-apply it.
    // Note: statusFilters and isModalOpen are seeded directly from location.state
    // via their useState initializers above (see declarations). We deliberately
    // don't clear location.state afterward — doing so via navigate() risks
    // forcing a remount on routers that key routes by location, which would
    // reset those initializers to their empty defaults right after they'd
    // just been correctly set.

    // Lazily load customers and stores the first time the creation order modal opens.
    // The `customersLoaded` / `storesLoaded` guards prevent re-fetching on
    // further modal opens within the same session.
    useEffect(() => {
        if (!isModalOpen) return;
        const load = async () => {
            await Promise.all([
                customersLoaded ? Promise.resolve() : fetchCustomers(),
                storesLoaded    ? Promise.resolve() : fetchStores(),
            ]);
            setCustomersLoaded(true);
            setStoresLoaded(true);
        };
        void load();
    }, [isModalOpen, customersLoaded, storesLoaded, fetchCustomers, fetchStores]);

    // Reset to page 1 whenever any filter or tab changes to avoid landing on a
    // non-existent page when the new result set has fewer pages.
    useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilters, typeFilter, deliveryTypeFilter, activeTab]);

    // Close both the status dropdown and the action menu when a click occurs
    // outside their respective containers.
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
                setIsStatusDropdownOpen(false);
            }
            const targetNode = event.target as Node;
            if (dropdownOpenId !== null) {
                const activeButton = actionButtonRefs.current[dropdownOpenId];
                const activeMenu   = actionMenuRefs.current[dropdownOpenId];
                if (activeButton && !activeButton.contains(targetNode) && activeMenu && !activeMenu.contains(targetNode)) {
                    setDropdownOpenId(null);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [dropdownOpenId]);

    // Keep the portal-rendered action menu anchored to its ⋮ button when the
    // user scrolls or resizes the window. The listeners are attached only while
    // a menu is open and cleaned up when it closes.
    useEffect(() => {
        if (dropdownOpenId === null) return;
        const updatePosition = () => {
            const button = actionButtonRefs.current[dropdownOpenId];
            if (!button) return;
            const rect = button.getBoundingClientRect();
            setActionMenuPosition({
                top:  rect.bottom + 8,
                left: Math.max(8, Math.min(rect.right - 192, window.innerWidth - 200)),
            });
        };
        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [dropdownOpenId]);

    // ── Handlers ───────────────────────────────────────────────────────────────

    const formatDate = (d?: string | null) => d ? new Date(d).toLocaleString() : '–';

    // Handles all scalar field changes in the creation order form.
    // `parcel_count` is coerced to an integer; `notes` is capped at 1500 characters.
    const handleOrderChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setOrderData(p => ({
            ...p,
            [name]: name === 'parcel_count' ? parseInt(value) || 0 : name === 'notes' ? value.slice(0, 1500) : value,
        }));
    };

    const handleOrderItemsChange = (items: OrderFormData['order_items']) => {
        setOrderData(p => ({ ...p, order_items: items }));
    };

    // Type guards used by handleCustomerChange to route field updates to the
    // correct part of the newCustomer state object.
    const isCustomerField = (f: string): f is keyof Omit<Customer, 'id' | 'address'> => ['first_name', 'last_name', 'cellphone'].includes(f);
    const isAddressField  = (f: string): f is keyof Address => ['address_1', 'address_2', 'suburb', 'city', 'postcode'].includes(f);

    // Handles input changes in the new customer form.
    // Dot-notation field names (e.g. "address.suburb") are split and routed
    // to the nested address object; top-level fields update directly.
    const handleCustomerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            if (parent === 'address' && isAddressField(child)) setNewCustomer(p => ({ ...p, address: { ...p.address, [child]: value } }));
        } else if (isCustomerField(name)) {
            setNewCustomer(p => ({ ...p, [name]: value }));
        }
    };

    const toggleStatusFilter = (s: string) => setStatusFilters(p => p.includes(s) ? p.filter(x => x !== s) : [...p, s]);
    const clearStatusFilters = () => setStatusFilters([]);

    // Opens the assign-driver modal for a given order.
    // Pre-populates the destination with the order's existing delivery address
    // and fetches all addresses on file for the customer.
    const openAssignFor = async (order: Order) => {
        setSelectedOrder(order);
        const initialAddressId = order.delivery_address_id || order.customer?.delivery_address || null;
        setNewDelivery({ destination_id: initialAddressId, driver_id: null });
        if (order.customer_id) {
            const fetched = await fetchAddressesForCustomer(order.customer_id);
            setAddresses(fetched);
        }
        setIsCreateDeliveryModalOpen(true);
    };

    // Submits the assign-driver form. On success, closes the modal and
    // refreshes the order table to reflect the new DRIVER_ASSIGNED status.
    const handleDeliverySubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedOrder) return;
        if (!newDelivery.destination_id || !newDelivery.driver_id) return;
        try {
            setLoading(true);
            const assigned = await assignDriverToOrder({
                orderId:       String(selectedOrder.id),
                customerId:    String(selectedOrder.customer_id),
                destinationId: newDelivery.destination_id,
                driverId:      newDelivery.driver_id,
            });
            if (assigned) {
                setIsCreateDeliveryModalOpen(false);
                await loadOrdersPage();
            }
        } finally {
            setLoading(false);
        }
    };

    // Switching tabs clears status filters so stale filter state from one tab
    // doesn't carry over and return zero results on the other.
    const switchToOpenTab      = () => { setActiveTab('open');      setStatusFilters([]); setIsStatusDropdownOpen(false); };
    const switchToDeliveredTab = () => { setActiveTab('delivered'); setStatusFilters([]); setIsStatusDropdownOpen(false); };

    // Computes the status filter button label:
    // "All Statuses" → one status name → "N statuses selected"
    const statusFilterLabel =
        statusFilters.length === 0 ? 'All Statuses'
            : statusFilters.length === 1 ? STATUS_OPTIONS.find(s => s.value === statusFilters[0])?.label ?? '1 selected'
                : `${statusFilters.length} statuses selected`;

    // Handles the full create-order flow:
    //   1. If no existing customer is selected, creates a new customer + address and geocodes it.
    //   2. Creates the order record.
    //   3. Inserts each line item, resolving the price record per product/store if needed.
    //   4. Advances the order to AWAITING_PACKING.
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            let newOrderCustomerId: string | number | undefined;
            let newOrderDeliveryAddressId: string | null = null;

            if (!orderData.customer_id && newCustomer.first_name && newCustomer.last_name) {
                // New customer path: create the customer, geocode their address,
                // create the address row, then set it as their primary delivery address.
                const createdCustomer = await createCustomer({
                    first_name: newCustomer.first_name,
                    last_name:  newCustomer.last_name,
                    cellphone:  newCustomer.cellphone,
                    delivery_address: null,
                });
                if (!createdCustomer?.id) {
                    error('Failed to create customer');
                    return;
                }
                newOrderCustomerId = createdCustomer.id;

                const queryLine = [newCustomer.address.address_1, newCustomer.address.suburb, newCustomer.address.city].filter(Boolean).join(', ');
                const coords    = await geocodeAddress(queryLine);

                const createdAddress = await createAddress({
                    ...newCustomer.address,
                    customer_id: createdCustomer.id,
                    latitude:  coords ? String(coords.lat) : null,
                    longitude: coords ? String(coords.lng) : null,
                });
                if (!createdAddress?.id) {
                    error('Failed to create customer address');
                    return;
                }
                newOrderDeliveryAddressId = createdAddress.id;
                await setCustomersPrimaryAddress(createdCustomer.id, createdAddress.id);
            } else {
                // Existing customer path: use the IDs already selected in the form.
                newOrderCustomerId        = orderData.customer_id;
                newOrderDeliveryAddressId = orderData.delivery_address_id || null;
            }

            if (!newOrderCustomerId) return;

            // Sum line item totals to set the order's `total` field.
            const orderItemsTotal = (orderData.order_items || []).reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);

            const createdOrder = await createOrder({
                reference:           orderData.reference,
                parcel_count:        orderData.parcel_count,
                customer_id:         newOrderCustomerId,
                delivery_address_id: newOrderDeliveryAddressId,
                type:                'chat',
                notes:               (orderData.notes || '').slice(0, 1500),
                store_id:            orderData.store_id || null,
                total:               orderItemsTotal,
                delivery_fee:        orderData.delivery_fee,
                delivery_type:       orderData.delivery_type,
                collection_point_id: orderData.collection_point_id || null,
            } as OrderFormData);

            if (createdOrder?.id) {
                // Insert each line item. If no price record ID supplied by the
                // form (manual entry), look it up by product + store combination.
                if (orderData.order_items && orderData.order_items.length > 0) {
                    for (const item of orderData.order_items) {
                        const priceRecordId = item.product_prices_id
                            ?? (await fetchProductPriceRecordFromDb(item.product_id, orderData.store_id))?.id;

                        if (!priceRecordId) {
                            error(`Price not found for ${item.product_name} at this store.`);
                            continue;
                        }

                        try {
                            await insertOrderItemFromDb(createdOrder.id, item.product_id, priceRecordId, item.quantity);
                        } catch {
                            error(`Failed to add ${item.product_name} to order`);
                        }
                    }
                }
                // Move the order into the packing queue once all items are inserted.
                await updateOrderStatus(String(createdOrder.id), 'AWAITING_PACKING');
                success('Order created successfully!');
            }
            handleCloseModal();
        } catch (err) {
            error(err instanceof Error ? err.message : 'Failed to create order');
        } finally {
            await loadOrdersPage();
            setLoading(false);
        }
    };

    // Cancels the order stored in `orderToCancel`, shows a toast on success,
    // and refreshes the table, so the updated status is immediately visible.
    const handleCancelOrder = async () => {
        if (!orderToCancel) return;
        setCancelLoading(true);
        try {
            const updated = await updateOrderStatus(String(orderToCancel.id), 'ORDER_CANCELLED');
            if (!updated) {
                error('Failed to cancel order');
                return;
            }
            await loadOrdersPage();
            success('Order cancelled');
            setOrderToCancel(null);
            setDropdownOpenId(null);
        } finally {
            setCancelLoading(false);
        }
    };

    // Resets all create-order modal states so it opens clean next time.
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setShowNewCustomerForm(false);
        setOrderData({ reference: '', parcel_count: 1, customer_id: '', delivery_address_id: '', type: '', notes: '', store_id: '', delivery_fee: 0, delivery_type: '', collection_point_id: '' });
        setNewCustomer({ first_name: '', last_name: '', cellphone: '', delivery_address: null, address: { address_1: '', address_2: '', suburb: '', city: '', postcode: '' } });
    };

    const goToOrder = (id: string | number) => navigate(`/orders/${id}`);

    // Derived pagination values used by the table and TablePagination component.
    const totalItems      = ordersTotalCount;
    const totalPages      = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
    const paginatedOrders = ordersPageRows ?? [];

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) { setCurrentPage(newPage); window.scrollTo({ top: 0, behavior: 'smooth' }); }
    };

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold text-stone-900">Orders</h1>
                <p className="mt-1 text-sm text-stone-500">Monitor and manage customer orders across all channels</p>
            </div>

            {/* Tabs — switches between active orders and the delivered/archived view */}
            <div className="flex">
                <div className="inline-flex overflow-hidden rounded-lg border border-stone-300 bg-white shadow-sm">
                    <button type="button" onClick={switchToOpenTab}
                            className={`px-4 py-2 text-sm font-medium transition-colors ${activeTab === 'open' ? 'bg-stone-900 text-white' : 'text-stone-700 hover:bg-stone-50'}`}>
                        Open
                    </button>
                    <button type="button" onClick={switchToDeliveredTab}
                            className={`px-4 py-2 text-sm font-medium transition-colors ${activeTab === 'delivered' ? 'bg-stone-900 text-white' : 'text-stone-700 hover:bg-stone-50'}`}>
                        Delivered
                    </button>
                </div>
            </div>

            {/* Filters — search, fulfilment type, order type, status multi-select, and create button */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:flex-wrap">
                <div className="relative min-w-[280px] flex-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
                    <input type="text" placeholder="Search by order ref or customer name…" value={searchQuery}
                           onChange={e => setSearchQuery(e.target.value)}
                           className="w-full rounded-lg border border-stone-300 bg-white py-2.5 pl-10 pr-4 text-sm shadow-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[#ee2b74]" />
                </div>
                <div className="relative w-full sm:w-48">
                    <select value={deliveryTypeFilter} onChange={e => setDeliveryTypeFilter(e.target.value)}
                            className="block w-full appearance-none rounded-lg border border-stone-300 bg-white px-4 py-2.5 pr-10 text-sm text-stone-700 shadow-sm outline-none focus:ring-2 focus:ring-[#ee2b74]">
                        <option value="All">All Fulfilment</option>
                        <option value="delivery">Delivery</option>
                        <option value="collection">Collection</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-stone-400">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                    </div>
                </div>
                <div className="relative w-full sm:w-40">
                    <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                            className="block w-full appearance-none rounded-lg border border-stone-300 bg-white px-4 py-2.5 pr-10 text-sm text-stone-700 shadow-sm outline-none focus:ring-2 focus:ring-[#ee2b74]">
                        <option value="All">All Types</option>
                        <option value="ecom">Ecom Orders</option>
                        <option value="chat">Chat Orders</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-stone-400">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                    </div>
                </div>

                {/* Status multi-select dropdown */}
                <div ref={statusDropdownRef} className="relative">
                    <button onClick={() => setIsStatusDropdownOpen(p => !p)}
                            className="flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-4 py-2.5 text-sm text-stone-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#ee2b74]">
                        <span>{statusFilterLabel}</span>
                        <svg className="h-4 w-4 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    {isStatusDropdownOpen && (
                        <div className="absolute left-0 z-20 mt-2 w-60 rounded-xl border border-stone-200 bg-white shadow-lg">
                            <div className="flex items-center justify-between border-b border-stone-100 px-3 py-2">
                                <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">Filter by status</span>
                                <button onClick={clearStatusFilters} className="text-xs font-medium text-[#ee2b74] hover:underline">Clear all</button>
                            </div>
                            <div className="max-h-64 overflow-y-auto py-2">
                                {STATUS_OPTIONS.map(opt => (
                                    <label key={opt.value} className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm text-stone-700 hover:bg-stone-50">
                                        <input type="checkbox" checked={statusFilters.includes(opt.value)} onChange={() => toggleStatusFilter(opt.value)}
                                               className="h-4 w-4 rounded border-stone-300 accent-[#ee2b74]" />
                                        {opt.label}
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <button onClick={() => setIsModalOpen(true)}
                        className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-[#ee2b74] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#d4245f] active:scale-95 sm:ml-auto">
                    <PlusIcon className="h-4 w-4" /> Capture New Order
                </button>
            </div>

            {/* Orders table
                Three states:
                  1. ordersPageRows === null → skeleton rows (loading)
                  2. paginatedOrders.length === 0 → empty state message
                  3. Otherwise, → populated table + pagination
            */}
            <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
                {ordersPageRows === null ? (
                    // Skeleton — shown while the page fetch is in flight.
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="border-b border-stone-200 bg-stone-50">
                            <tr>{['Order Ref','Customer','Type / Fulfilment','Status','Created','Action'].map(h => (
                                <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">{h}</th>
                            ))}</tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <tr key={i} className="animate-pulse">
                                    <td className="px-4 py-3"><div className="h-4 w-28 bg-stone-100 rounded" /></td>
                                    <td className="px-4 py-3"><div className="h-4 w-32 bg-stone-100 rounded" /></td>
                                    <td className="px-4 py-3"><div className="h-5 w-24 bg-stone-100 rounded-full" /></td>
                                    <td className="px-4 py-3"><div className="h-5 w-28 bg-stone-100 rounded-full" /></td>
                                    <td className="px-4 py-3"><div className="h-4 w-24 bg-stone-100 rounded" /></td>
                                    <td className="px-4 py-3"><div className="h-7 w-7 bg-stone-100 rounded ml-auto" /></td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                ) : paginatedOrders.length === 0 ? (
                    <div className="px-4 py-10 text-center text-sm text-stone-500">
                        {activeTab === 'delivered' ? 'No delivered orders found.' : 'No orders found.'}
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="border-b border-stone-200 bg-stone-50">
                                <tr>
                                    {['Order Ref','Customer','Type / Fulfilment','Status','Created','Action'].map(h => (
                                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">{h}</th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-stone-100">
                                {paginatedOrders.map(order => {
                                    const hasCake = cakeOrderIds.has(String(order.id));
                                    return (
                                        // Clicking the row navigates to the order detail page.
                                        // e.stopPropagation() on child buttons prevents row navigation
                                        // when interacting with the action menu.
                                        <tr key={order.id} onClick={() => goToOrder(order.id)}
                                            className="cursor-pointer transition hover:bg-stone-50">

                                            {/* Order reference + optional shared-basket and cake badges */}
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-stone-900">{order.reference}</span>
                                                    {order.shared_basket_id && (
                                                        <span title="Shared Basket"
                                                              className="inline-flex items-center gap-1 rounded-md bg-pink-50 border border-pink-100 px-1.5 py-0.5">
                                                            <ShoppingCartIcon className="h-3 w-3 text-[#ee2b74]" />
                                                        </span>
                                                    )}
                                                    {hasCake && (
                                                        <span title="Includes cake order"
                                                              className="inline-flex items-center rounded-md bg-pink-50 border border-pink-100 px-1.5 py-0.5">
                                                            <CakeIcon className="h-3 w-3 text-[#ee2b74]" />
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Customer name */}
                                            <td className="px-4 py-3 text-stone-700">
                                                {order.customer?.first_name} {order.customer?.last_name}
                                            </td>

                                            {/* Order type badge + delivery/collection fulfilment badge */}
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <OrderTypeBadge type={order.type} />
                                                    {order.delivery_type && (
                                                        <span className={`rounded-md border px-2 py-0.5 text-xs capitalize ${order.delivery_type === 'collection' ? 'border-teal-100 bg-teal-50 text-teal-700' : 'border-sky-100 bg-sky-50 text-sky-700'}`}>
                                                            {order.delivery_type === 'collection' ? 'collection' : 'delivery'}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Status badge */}
                                            <td className="px-4 py-3"><StatusBadge status={order.status} /></td>

                                            {/* Created timestamp */}
                                            <td className="px-4 py-3 text-stone-500 text-xs">{formatDate(order.created_at)}</td>

                                            {/* Action menu — rendered in a portal so it isn't clipped by table overflow */}
                                            <td className="px-4 py-3 text-right overflow-visible">
                                                <button
                                                    ref={el => { actionButtonRefs.current[order.id] = el; }}
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        setActionMenuPosition(getMenuPosition(e.currentTarget));
                                                        setDropdownOpenId(prev => prev === order.id ? null : order.id);
                                                    }}
                                                    className="rounded-md p-1 transition-colors hover:bg-stone-200" type="button">
                                                    <EllipsisVerticalIcon className="h-5 w-5 text-stone-500" />
                                                </button>
                                                {dropdownOpenId === order.id && createPortal(
                                                    <div
                                                        ref={el => { actionMenuRefs.current[order.id] = el; }}
                                                        className="fixed z-[9999] w-48 rounded-xl border border-stone-200 bg-white py-2 shadow-xl divide-y divide-stone-100"
                                                        style={{ top: actionMenuPosition.top, left: actionMenuPosition.left }}>

                                                        {/* Always-present view details action */}
                                                        <button type="button"
                                                                onClick={e => { e.stopPropagation(); goToOrder(order.id); setDropdownOpenId(null); }}
                                                                className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-stone-700 hover:bg-stone-50">
                                                            <EyeIcon className="h-4 w-4 text-stone-400" /> View Details
                                                        </button>

                                                        {/* Status-contextual quick actions (Assign Packer, Track Delivery) */}
                                                        {getActionsByStatus(order.status).map((action, i) => (
                                                            <button key={i} type="button"
                                                                    onClick={e => {
                                                                        e.stopPropagation();
                                                                        if (action.label === 'Assign Driver') { void openAssignFor(order); }
                                                                        else { navigate(`/orders/${order.id}`, { state: { activeTab: action.tab } }); }
                                                                        setDropdownOpenId(null);
                                                                    }}
                                                                    className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-[#ee2b74] hover:bg-pink-50">
                                                                <action.icon className="h-4 w-4" /> {action.label}
                                                            </button>
                                                        ))}

                                                        {/* Cancel order — hidden for statuses where cancellation is no longer possible */}
                                                        {!NON_CANCELLABLE_STATUSES.includes(order.status?.toUpperCase()) && (
                                                            <button type="button"
                                                                    onClick={e => {
                                                                        e.stopPropagation();
                                                                        setOrderToCancel(order);
                                                                        setDropdownOpenId(null);
                                                                    }}
                                                                    className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50">
                                                                <TrashIcon className="h-4 w-4" /> Cancel Order
                                                            </button>
                                                        )}
                                                    </div>,
                                                    document.body
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                        <TablePagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalItems={totalItems}
                            itemsPerPage={ITEMS_PER_PAGE}
                            onPageChange={handlePageChange}
                        />
                    </>
                )}
            </div>

            {/* Assign-driver modal — opened via openAssignFor from the action menu */}
            {isCreateDeliveryModalOpen && (
                <CreateDeliveryModal
                    selectedOrder={selectedOrder}
                    isOpen={isCreateDeliveryModalOpen}
                    onClose={() => setIsCreateDeliveryModalOpen(false)}
                    newDelivery={newDelivery}
                    onDeliveryChange={e => setNewDelivery(p => ({ ...p, destination_id: e.target.value }))}
                    onDriverChange={e => setNewDelivery(p => ({ ...p, driver_id: e.target.value }))}
                    onSubmit={handleDeliverySubmit}
                    loading={loading || orderLoading}
                    customerAddresses={addresses}
                />
            )}

            {/* Create order modal — opened via the "Capture New Order" button or deep-link state */}
            <CreateOrderModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                orderData={orderData}
                newCustomer={newCustomer}
                showNewCustomerForm={showNewCustomerForm}
                customers={customers}
                onOrderChange={handleOrderChange}
                onOrderItemsChange={handleOrderItemsChange}
                onCustomerChange={handleCustomerChange}
                onToggleCustomerForm={() => setShowNewCustomerForm(!showNewCustomerForm)}
                onSubmit={handleSubmit}
                loading={loading || orderLoading}
                stores={stores}
            />

            {/* Cancel order confirmation — replaces window.confirm for destructive order cancellation */}
            <ConfirmModal
                isOpen={Boolean(orderToCancel)}
                title="Cancel order?"
                message={
                    orderToCancel
                        ? `Are you sure you want to cancel order ${orderToCancel.reference}? This action may affect fulfilment and delivery workflows.`
                        : 'Are you sure you want to cancel this order?'
                }
                onCancel={() => setOrderToCancel(null)}
                onConfirm={handleCancelOrder}
                loading={cancelLoading}
                confirmLabel="Cancel order"
            />
        </div>
    );
};

export default Orders;